"use strict";
/**
 * Copyright © 2020 Johnson & Johnson
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __createBinding = this && this.__createBinding || (Object.create ? function (o, m, k, k2) {
  if (k2 === undefined) k2 = k;
  Object.defineProperty(o, k2, {
    enumerable: true,
    get: function get() {
      return m[k];
    }
  });
} : function (o, m, k, k2) {
  if (k2 === undefined) k2 = k;
  o[k2] = m[k];
});

var __setModuleDefault = this && this.__setModuleDefault || (Object.create ? function (o, v) {
  Object.defineProperty(o, "default", {
    enumerable: true,
    value: v
  });
} : function (o, v) {
  o["default"] = v;
});

var __importStar = this && this.__importStar || function (mod) {
  if (mod && mod.__esModule) return mod;
  var result = {};
  if (mod != null) for (var k in mod) {
    if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
  }

  __setModuleDefault(result, mod);

  return result;
};

var __spreadArrays = this && this.__spreadArrays || function () {
  for (var s = 0, i = 0, il = arguments.length; i < il; i++) {
    s += arguments[i].length;
  }

  for (var r = Array(s), k = 0, i = 0; i < il; i++) {
    for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++) {
      r[k] = a[j];
    }
  }

  return r;
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.asBreadcrumbSource = exports.BreadcrumbContextProvider = exports.useBreadcrumbContext = void 0;

var react_1 = __importStar(require("react"));

var core_1 = require("@bodiless/core");

var mobx_react_lite_1 = require("mobx-react-lite");

var lodash_1 = require("lodash");

var BreadcrumbStore_1 = require("./BreadcrumbStore");

var BreadcrumbStoreProvider_1 = require("./BreadcrumbStoreProvider");

var breadcrumbContext = react_1.createContext(undefined);

exports.useBreadcrumbContext = function () {
  return react_1.useContext(breadcrumbContext);
};

exports.BreadcrumbContextProvider = breadcrumbContext.Provider;

var isSSR = function isSSR() {
  return !(typeof window !== 'undefined' && window.document && window.document.createElement);
};
/**
 * Creates an HOC which specifies that a wrapped component is a breadcrumb. The HOC
 * will read link and title from the specified nodekeys and will push link and title
 * to the breadcrumb store. Once the wrapped component is unmounted, the corresponding link
 * and title are deleted from the breadcrumb store
 *
 * @param settings The title and link nodekeys defining where to locate the link and title nodes.
 *
 * @return An HOC which defines the wrapped component as a breadcrumb.
 */


var asBreadcrumb = function asBreadcrumb(_a) {
  var linkNodeKey = _a.linkNodeKey,
      titleNodeKey = _a.titleNodeKey;
  return function (Component) {
    var AsBreadcrumb = mobx_react_lite_1.observer(function (props) {
      var current = exports.useBreadcrumbContext();
      var store = BreadcrumbStoreProvider_1.useBreadcrumbStore();
      var node = core_1.useNode().node;
      if (store === undefined) return react_1.default.createElement(Component, __assign({}, props));
      var titleNode = node.child(titleNodeKey);
      var linkNode = node.child(linkNodeKey); // We need an id which will be the same for all breadcrumb sources which
      // render the same data.  Node path works well for this.

      var id = node.path.join('$');
      var item = new BreadcrumbStore_1.BreadcrumbItem({
        uuid: id,
        title: {
          data: titleNode.data,
          nodePath: __spreadArrays(node.path, [titleNodeKey]).join('$')
        },
        link: {
          data: linkNode.data.href,
          nodePath: __spreadArrays(node.path, [linkNodeKey]).join('$')
        },
        parent: current,
        store: store
      }); // During SSR we need to populate the store on render, bc effects are not executed.

      if (isSSR()) {
        store.setItem(item);
      } else {
        // Normally, conditional hooks violate the "rules of hooks", but here
        // the condition evaluates the same in a given render environment, and
        // we can't/shouldn't call useLayoutEffect during SSR.
        react_1.useLayoutEffect(function () {
          store.setItem(item);
        }, [titleNode.data, linkNode.data]); // deleting item from store on unmount

        react_1.useLayoutEffect(function () {
          return function () {
            store.deleteItem(id);
          };
        }, []);
      }

      return react_1.default.createElement(exports.BreadcrumbContextProvider, {
        value: item
      }, react_1.default.createElement(Component, __assign({}, props)));
    });
    return AsBreadcrumb;
  };
};
/**
 * Use this HOC to wrap a menu so as to generate data for breadcrumbs
 * and menu trails. Must be rendered within a BreadcrumbStoreContext
 *
 * @param Component
 * The component providing the menu data structure.
 *
 * @return
 * A version of the component which populates an enclosing
 */


var asBreadcrumbSource = function asBreadcrumbSource(withMenuDesign) {
  return function (settings) {
    return function (Component) {
      var Source = withMenuDesign({
        Item: asBreadcrumb(settings)
      })(Component);
      var SSRSource = lodash_1.flow(withMenuDesign({
        Item: asBreadcrumb(settings)
      }), BreadcrumbStoreProvider_1.asHiddenBreadcrumbSource)(Component);

      var AsBreadcrumbSource = function AsBreadcrumbSource(props) {
        return react_1.default.createElement(react_1.default.Fragment, null, isSSR() && react_1.default.createElement(SSRSource, __assign({}, props)), react_1.default.createElement(Source, __assign({}, props)));
      };

      return AsBreadcrumbSource;
    };
  };
};

exports.asBreadcrumbSource = asBreadcrumbSource;
exports.default = asBreadcrumb;